import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Brain,
  Send,
  Loader,
  AlertCircle,
  BookOpen,
  Lightbulb,
  Users,
  Target,
  ArrowLeft,
  Trash2,
  Sparkles,
  MessageSquare,
} from "lucide-react";
import Layout from "../../components/Layout/Layout";
import { getApiBaseUrl } from "../../services/apiBase";
import "./TeacherTutorAgent.css";

const API_BASE_URL = getApiBaseUrl();

const TeacherTutorAgent = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [context, setContext] = useState({
    subject: "",
    grade_level: "",
    topic: "",
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
    
    // Add welcome message
    setMessages([
      {
        role: "assistant",
        content: `👋 **Welcome to Your Teaching Assistant!**

I'm here to support you with:
• **Pedagogical strategies** and teaching methods
• **Lesson planning** and curriculum design
• **Classroom management** techniques
• **Assessment** and evaluation approaches
• **Differentiated instruction** for diverse learners
• **Student engagement** strategies
• **Educational technology** integration

How can I help you today?`,
        timestamp: new Date().toISOString(),
      },
    ]);
  }, []);

  const getAuthToken = () => {
    return (
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token")
    );
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: "teacher",
      content: input,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/teacher/tutor/ask`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          question: input,
          context: context,
        }),
      });

      if (response.status === 401) {
        navigate("/login");
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to get response from teaching assistant");
      }

      const data = await response.json();

      const assistantMessage = {
        role: "assistant",
        content: data.response,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      console.error("Error:", err);
      setError(err.message);
      
      const errorMessage = {
        role: "assistant",
        content: "I apologize, but I encountered an error. Please try again or rephrase your question.",
        timestamp: new Date().toISOString(),
        isError: true,
      };
      
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearConversation = () => {
    if (window.confirm("Are you sure you want to clear the conversation?")) {
      setMessages([
        {
          role: "assistant",
          content: "Conversation cleared. How can I assist you?",
          timestamp: new Date().toISOString(),
        },
      ]);
    }
  };

  const formatMessage = (content) => {
    // Simple markdown-like formatting
    return content
      .split("\n")
      .map((line, idx) => {
        // Bold text
        line = line.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
        // Bullet points
        if (line.trim().startsWith("•") || line.trim().startsWith("-")) {
          return `<li key="${idx}">${line.replace(/^[•-]\s*/, "")}</li>`;
        }
        // Numbered lists
        if (/^\d+\./.test(line.trim())) {
          return `<li key="${idx}">${line.replace(/^\d+\.\s*/, "")}</li>`;
        }
        return `<p key="${idx}">${line || "<br/>"}</p>`;
      })
      .join("");
  };

  // Quick suggestion buttons
  const quickSuggestions = [
    {
      icon: <Users size={16} />,
      text: "How can I engage reluctant learners?",
      query: "What are effective strategies to engage students who seem reluctant or unmotivated in class?",
    },
    {
      icon: <Target size={16} />,
      text: "Differentiated instruction tips",
      query: "Can you provide strategies for differentiated instruction to meet diverse learning needs in my classroom?",
    },
    {
      icon: <Lightbulb size={16} />,
      text: "Formative assessment ideas",
      query: "What are some creative formative assessment techniques I can use during lessons?",
    },
    {
      icon: <BookOpen size={16} />,
      text: "Classroom management strategies",
      query: "What are effective classroom management strategies for maintaining a positive learning environment?",
    },
  ];

  const handleSuggestionClick = (query) => {
    setInput(query);
    inputRef.current?.focus();
  };

  return (
    <Layout>
      <div className="teacher-tutor-agent">
        {/* Header */}
        <div className="tutor-header">
          <div className="tutor-header-top">
            <button className="back-button" onClick={() => navigate("/teacher-dashboard")}>
              <ArrowLeft size={18} />
              Back
            </button>
            <button className="clear-button" onClick={clearConversation} title="Clear conversation">
              <Trash2 size={18} />
            </button>
          </div>

          <div className="tutor-header-content">
            <div className="tutor-header-icon">
              <Brain size={24} />
            </div>
            <div>
              <h1>AI Teaching Assistant</h1>
              <p>Get pedagogical support and teaching strategies</p>
            </div>
          </div>

          {/* Compact context inputs */}
          <div className="context-inputs-compact">
            <input
              type="text"
              placeholder="Subject"
              value={context.subject}
              onChange={(e) => setContext({ ...context, subject: e.target.value })}
              className="context-input-small"
            />
            <input
              type="text"
              placeholder="Grade Level"
              value={context.grade_level}
              onChange={(e) => setContext({ ...context, grade_level: e.target.value })}
              className="context-input-small"
            />
            <input
              type="text"
              placeholder="Topic"
              value={context.topic}
              onChange={(e) => setContext({ ...context, topic: e.target.value })}
              className="context-input-small"
            />
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <AlertCircle size={20} />
            <p>{error}</p>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Main Chat Container */}
        <div className="chat-main-container">
          {/* Scrollable Messages Area */}
          <div className="messages-scroll-area">
            {messages.map((message, index) => (
              <div key={index} className={`message ${message.role}`}>
                <div className="message-avatar">
                  {message.role === "assistant" ? (
                    <Brain size={20} />
                  ) : (
                    <Users size={20} />
                  )}
                </div>
                <div className="message-bubble">
                  <div
                    className="message-text"
                    dangerouslySetInnerHTML={{
                      __html: formatMessage(message.content),
                    }}
                  />
                  <div className="message-time">
                    {new Date(message.timestamp).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              </div>
            ))}

            {loading && (
              <div className="message assistant loading">
                <div className="message-avatar">
                  <Brain size={20} />
                </div>
                <div className="message-bubble">
                  <Loader className="spinner" size={18} />
                  <span>Thinking...</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Suggestions - Only show when no messages */}
          {messages.length <= 1 && (
            <div className="suggestions-section">
              <p className="suggestions-title">
                <Sparkles size={16} />
                Quick suggestions
              </p>
              <div className="suggestions-grid">
                {quickSuggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    className="suggestion-button"
                    onClick={() => handleSuggestionClick(suggestion.query)}
                  >
                    {suggestion.icon}
                    <span>{suggestion.text}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="input-area">
            <div className="input-wrapper">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask for teaching strategies, lesson ideas, classroom management tips..."
                disabled={loading}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || loading}
                className="send-button"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TeacherTutorAgent;
